import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../components/ui/PageHeader.jsx';
import { superAdminApi } from '../../api/superAdmin.js';
import { DeleteDealerModal } from '../../components/SuperAdmin/DeleteDealerModal.jsx';
import { CreateAccountModal } from '../../components/SuperAdmin/CreateAccountModal.jsx';
import {
    Bot, Globe2, ChevronRight, RefreshCw, Trash2, Phone, Server, UserPlus,
} from 'lucide-react';
import { Card } from '../../components/ui/Card.jsx';
import { coverFromSeed, initialsOf } from '../../components/IvrAgents/coverArt.js';
import { getCached, setCached } from './pageCache.js';

/**
 * /admin — 超管中心：經銷商 → tenant 總覽
 *
 * 設計：密度優先的 ledger 風格 —— 全域統計列（hairline 分隔，不加卡框）、
 * 經銷商 = section（slim header + 計數），tenant = 表格列（單一框內以分隔線堆疊），
 * 整列可點進 AI 運維工作區。層級全靠字級/字重/間距，唯一 accent 留給互動。
 */
export default function TenantsOverviewPage() {
    const { t } = useTranslation('superAdmin');
    // 前端快取：切到別頁再回來不重抓（refresh / 按重新整理才重載）
    const [data, setData] = useState(() => getCached('sa:overview'));
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(!data);
    const [deleting, setDeleting] = useState(null);   // dealer object → modal
    const [creating, setCreating] = useState(false);  // 建立帳號 modal

    const load = () => {
        setLoading(true);
        superAdminApi.overview()
            .then((d) => { setData(setCached('sa:overview', d)); setError(null); })
            .catch(setError)
            .finally(() => setLoading(false));
    };
    useEffect(() => { if (!getCached('sa:overview')) load(); }, []);

    const stats = useMemo(() => {
        const dealers = data?.dealers || [];
        const businesses = dealers.flatMap((d) => d.businessAccounts || []);
        const allTenants = [...dealers, ...businesses].flatMap((a) => a.tenants || []);
        return {
            dealers: dealers.length,
            businesses: businesses.length,
            tenants: allTenants.length + (data?.orphanTenants?.length || 0),
            phones: allTenants.reduce((s, tn) => s + (tn.phoneCount || 0), 0),
        };
    }, [data]);

    return (
        <div>
            <PageHeader
                eyebrow="super admin · operations"
                title={t('overview.title')}
                description={t('overview.description')}
                actions={(
                    <div className="flex items-center gap-2">
                        <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-cream-300 text-sm text-ink-700 hover:bg-cream-100 hover:border-accent-300 transition focus-visible:outline-2 focus-visible:outline-accent-500">
                            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> {t('overview.refresh')}
                        </button>
                        <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md bg-ink-900 text-cream-50 text-sm font-medium hover:opacity-90 transition focus-visible:outline-2 focus-visible:outline-accent-500 focus-visible:outline-offset-2">
                            <UserPlus size={14} /> {t('create.button')}
                        </button>
                    </div>
                )}
            />

            {error && (
                <div className="mb-6 px-4 py-3 rounded-md border border-danger-500/30 bg-danger-100/40 text-sm text-danger-500">
                    {String(error.message || error)}
                </div>
            )}

            {/* ── 全域統計列：hairline 分隔，不加卡框 ── */}
            {data && (
                <div className="mb-10 flex items-stretch divide-x divide-cream-200 border-y border-cream-200">
                    <Stat label={t('overview.stat.dealers')} value={stats.dealers} />
                    <Stat label={t('overview.stat.businesses')} value={stats.businesses} />
                    <Stat label={t('overview.stat.tenants')} value={stats.tenants} />
                    <Stat label={t('overview.stat.phones')} value={stats.phones} />
                </div>
            )}

            {loading && !data ? (
                <div className="text-center py-20 text-ink-400 font-mono text-xs">Loading…</div>
            ) : (() => {
                // 已啟用 = 名下（含旗下商用帳號）至少一個 tenant 已設定 PBX（有 publicIp）；
                // PBX 尚未設定的經銷商收到「尚未啟用」精簡列
                const dealers = data?.dealers || [];
                const hasPbx = (d) => [...(d.tenants || []), ...(d.businessAccounts || []).flatMap((b) => b.tenants || [])]
                    .some((tn) => !!tn.pbx?.publicIp);
                const active = dealers.filter(hasPbx);
                const inactive = dealers.filter((d) => !hasPbx(d));
                return (
                    <div className="space-y-10">
                        {active.map((dealer) => (
                            <DealerSection key={dealer.uid} dealer={dealer} onDelete={() => setDeleting(dealer)} />
                        ))}
                        {data?.orphanTenants?.length > 0 && (
                            <section>
                                <header className="flex items-baseline gap-3 pb-2 border-b border-cream-200">
                                    <h2 className="heading-display text-base font-semibold text-ink-500">{t('overview.orphans')}</h2>
                                </header>
                                <TenantRows tenants={data.orphanTenants} />
                            </section>
                        )}
                        {inactive.length > 0 && (
                            <section>
                                <header className="flex items-baseline gap-3 pb-2 border-b border-cream-200">
                                    <h2 className="heading-display text-base font-semibold text-ink-500">{t('overview.groupInactive')}</h2>
                                    <span className="text-xs text-ink-400 tnum">{inactive.length}</span>
                                </header>
                                <div className="mt-3 rounded-lg border border-cream-200 bg-white/30 divide-y divide-cream-100">
                                    {inactive.map((dealer) => (
                                        <div key={dealer.uid} className="group flex items-center gap-3 px-4 py-2.5">
                                            <span className="text-sm text-ink-700">{dealer.email}</span>
                                            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-400">
                                                {t(`role.${dealer.accountRole}`, { defaultValue: dealer.accountRole })}
                                            </span>
                                            <span className="flex items-center gap-1.5 flex-wrap">
                                                {/* 沒 PBX 但有角色的（只做角色的客戶）一樣可點進去診斷 — 標出角色數 */}
                                                {[...(dealer.tenants || []), ...(dealer.businessAccounts || []).flatMap((b) => b.tenants || [])].map((tn) => (
                                                    <Link
                                                        key={tn.hostId}
                                                        to={`/admin/tenants/${tn.hostId}`}
                                                        title={t('overview.aiDiagnose')}
                                                        className="inline-flex items-center gap-1 font-mono text-[11px] px-1.5 py-0.5 rounded bg-cream-100 text-ink-500 hover:bg-accent-50 hover:text-accent-700 transition"
                                                    >
                                                        <Bot size={10} className="text-ink-400" />
                                                        {tn.hostId}
                                                        {tn.avatarCount > 0 && <span className="text-accent-700 tnum">· {t('overview.avatarCount', { n: tn.avatarCount })}</span>}
                                                    </Link>
                                                ))}
                                            </span>
                                            <button
                                                onClick={() => setDeleting(dealer)}
                                                title={t('delete.button')}
                                                className="ml-auto p-1.5 rounded text-ink-300 opacity-0 group-hover:opacity-100 hover:text-danger-500 hover:bg-danger-100/50 transition focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-danger-500"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}
                    </div>
                );
            })()}

            {deleting && (
                <DeleteDealerModal
                    dealer={deleting}
                    onClose={() => setDeleting(null)}
                    onDeleted={() => { setDeleting(null); load(); }}
                />
            )}
            {creating && (
                <CreateAccountModal
                    dealers={(data?.dealers || []).map((d) => ({
                        uid: d.uid,
                        email: d.email,
                        // 經銷商名稱：帳號 displayName → 第一個 tenant 公司名 → email 前綴
                        name: d.displayName || d.tenants?.[0]?.displayName || (d.email || '').split('@')[0],
                    }))}
                    onClose={() => setCreating(false)}
                    onCreated={load}
                />
            )}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="flex-1 px-6 py-4 first:pl-1">
            <div className="tnum heading-display text-3xl font-semibold text-ink-900 leading-none">{value}</div>
            <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-400">{label}</div>
        </div>
    );
}

function DealerSection({ dealer, onDelete }) {
    const { t } = useTranslation('superAdmin');
    const ownTenants = dealer.tenants || [];
    const bizBlocks = (dealer.businessAccounts || []).filter((b) => (b.tenants || []).length > 0 || true);

    return (
        <section>
            {/* ── 經銷商 header：slim、計數靠右、刪除 ghost ── */}
            <header className="group flex items-baseline gap-3 pb-2 border-b border-cream-200">
                <h2 className="heading-display text-lg font-semibold text-ink-900 tracking-tight">{dealer.email}</h2>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent-700">
                    {t(`role.${dealer.accountRole}`, { defaultValue: dealer.accountRole })}
                </span>
                <span className="ml-auto text-xs text-ink-400 tnum">
                    {t('overview.tenantCount', { n: ownTenants.length })}
                    {bizBlocks.length > 0 && <> · {t('overview.businessCount', { n: bizBlocks.length })}</>}
                </span>
                <button
                    onClick={onDelete}
                    title={t('delete.button')}
                    className="self-center p-1.5 rounded text-ink-300 opacity-0 group-hover:opacity-100 hover:text-danger-500 hover:bg-danger-100/50 transition focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-danger-500"
                >
                    <Trash2 size={14} />
                </button>
            </header>

            {ownTenants.length > 0
                ? <TenantRows tenants={ownTenants} ownerRole="dealer" />
                : <div className="py-4 text-xs text-ink-400 italic">{t('overview.noTenants')}</div>}

            {/* 旗下商用帳號（縮排子層） */}
            {bizBlocks.map((biz) => (
                <div key={biz.uid} className="mt-4 ml-5 pl-4 border-l-2 border-cream-200">
                    <div className="flex items-baseline gap-2 mb-1">
                        <span className="text-sm font-medium text-ink-700">{biz.email}</span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-400">{t('role.business')}</span>
                        {biz.sharedHostId && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-accent-50 text-accent-700 border border-accent-300/60 font-mono text-[9px] uppercase tracking-[0.12em] leading-none">
                                {t('overview.sharedBadge', { hostId: biz.sharedHostId })}
                            </span>
                        )}
                        {biz.seats && (
                            <span className="text-[11px] text-ink-400 tnum">AA {biz.seats.plan_01 ?? 0} · CS {biz.seats.plan_02 ?? 0}</span>
                        )}
                    </div>
                    {(biz.tenants || []).length > 0
                        ? <TenantRows tenants={biz.tenants} ownerRole="business" />
                        : <div className="py-2 text-xs text-ink-400 italic">{t('overview.noTenants')}</div>}
                </div>
            ))}
        </section>
    );
}

/** tenant 卡片網格 — 沿用 IVR Agents 的卡片語言（封面漸層 + 字首縮寫），資訊密度補齊
 *  啟動中（已設定 PBX）= 卡片；未啟動 = 精簡列收在卡片區下方 */
function TenantRows({ tenants, ownerRole }) {
    const { t } = useTranslation('superAdmin');
    const isLive = (tn) => !!(tn.pbx?.model || tn.pbx?.publicIp);
    const liveTenants = tenants.filter(isLive);
    const idleTenants = tenants.filter((tn) => !isLive(tn));
    return (
        <>
            {liveTenants.length > 0 && <TenantCards tenants={liveTenants} ownerRole={ownerRole} />}
            {idleTenants.length > 0 && (
                <div className="mt-3">
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400 mb-1.5">
                        {t('overview.tnIdle')} · <span className="tnum">{idleTenants.length}</span>
                    </div>
                    <div className="rounded-lg border border-cream-200 bg-white/30 divide-y divide-cream-100">
                        {idleTenants.map((tn) => (
                            <Link
                                key={tn.hostId}
                                to={`/admin/tenants/${tn.hostId}`}
                                className="flex items-center gap-2.5 px-4 py-2 group focus-visible:outline-2 focus-visible:outline-accent-500"
                            >
                                <span className="size-1.5 rounded-full bg-ink-300 shrink-0" />
                                <span className="font-mono text-[11px] text-ink-500">{tn.hostId}</span>
                                <span className="text-sm text-ink-700 truncate">{tn.displayName}</span>
                                {tn.region && <span className="font-mono text-[10px] uppercase text-ink-400">{tn.region}</span>}
                                {tn.avatarCount > 0 && (
                                    <span className="inline-flex items-center gap-1 text-[11px] text-ink-400 tnum">
                                        <Bot size={11} className="text-ink-300" />{t('overview.avatarCount', { n: tn.avatarCount })}
                                    </span>
                                )}
                                <span className="ml-auto text-[11px] text-ink-300 italic">{t('ops.noPbx')}</span>
                                <ChevronRight size={13} className="text-ink-300 group-hover:text-accent-500 transition" />
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}

function TenantCards({ tenants, ownerRole }) {
    const { t } = useTranslation('superAdmin');
    return (
        <div className="mt-4 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {tenants.map((tn) => {
                const phones = tn.phones || {};
                // 商用帳號的 tenant 封面用藍色系，跟經銷商（黃）一眼區分
                const cover = coverFromSeed(tn.hostId, ownerRole === 'business' ? 'business' : 'full');
                return (
                    <Link key={tn.hostId} to={`/admin/tenants/${tn.hostId}`} className="block group focus-visible:outline-2 focus-visible:outline-accent-500 rounded-xl">
                        <Card className="p-0 overflow-hidden relative transition group-hover:border-accent-300 group-hover:shadow-md" padded={false}>
                            {/* 封面：同 AvatarCard 的 seed 漸層 + 縮寫 */}
                            <div className="h-20 flex items-center justify-center relative" style={{ background: cover.gradient }}>
                                <div className="heading-display text-3xl font-semibold" style={{ color: cover.fg }}>
                                    {initialsOf(tn.displayName)}
                                </div>
                                <div className="absolute top-2 left-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/85 backdrop-blur-[2px]">
                                    <span className={`size-1.5 rounded-full ${tn.enabled ? 'bg-success-500' : 'bg-ink-300'}`} />
                                    <span className="font-mono text-[10px] text-ink-700">{tn.hostId}</span>
                                </div>
                                {tn.region && (
                                    <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/85 backdrop-blur-[2px] font-mono text-[10px] uppercase text-ink-700">
                                        <Globe2 size={10} /> {tn.region}
                                    </div>
                                )}
                            </div>
                            {/* 內文：名稱 + 帳號類型標記 + 兩行 meta */}
                            <div className="p-4">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-sm font-semibold text-ink-900 truncate">{tn.displayName}</span>
                                    {ownerRole && (
                                        <span className={`shrink-0 font-mono text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded ${ownerRole === 'business' ? 'bg-accent-50 text-accent-700' : 'bg-cream-100 text-ink-500'}`}>
                                            {t(`role.${ownerRole}`)}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-2 space-y-1">
                                    <div className="flex items-center gap-1.5 text-xs text-ink-500 tnum">
                                        <Phone size={12} className="text-ink-300 shrink-0" />
                                        {tn.phoneCount} {t('overview.exts')}
                                        {(phones.plan_01 > 0 || phones.plan_02 > 0) && (
                                            <span className="text-ink-400">· AA {phones.plan_01 || 0} / CS {phones.plan_02 || 0}</span>
                                        )}
                                        {tn.avatarCount > 0 && (
                                            <span className="inline-flex items-center gap-1 text-ink-400"><Bot size={12} className="text-ink-300" />{t('overview.avatarCount', { n: tn.avatarCount })}</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-xs text-ink-500 min-h-[1rem]">
                                        {/* 有 publicIp 就算已設定 PBX；沒填機型時退回顯示 IP（避免誤標「尚未設定」） */}
                                        {(tn.pbx?.model || tn.pbx?.publicIp)
                                            ? <><Server size={12} className="text-ink-300 shrink-0" /><span className="truncate">{tn.pbx.model || tn.pbx.publicIp}</span></>
                                            : <span className="text-ink-300 italic">{t('ops.noPbx')}</span>}
                                    </div>
                                </div>
                                {/* footer：AI 診斷 affordance */}
                                <div className="mt-3 pt-3 border-t border-cream-100 flex items-center text-xs font-medium text-accent-700">
                                    <Bot size={13} className="mr-1.5" /> {t('overview.aiDiagnose')}
                                    <ChevronRight size={13} className="ml-auto text-ink-300 group-hover:text-accent-500 group-hover:translate-x-0.5 transition-transform" />
                                </div>
                            </div>
                        </Card>
                    </Link>
                );
            })}
        </div>
    );
}
